import { createFeatureSelector, createSelector } from '@ngrx/store';
import { APPROVAL_FEATURE_KEY, ApprovalState } from './approval.reducer';

/**
 * Feature selector — keyed off `APPROVAL_FEATURE_KEY` so the registration
 * in `app.config.ts` and the selector stay in lock-step.
 */
export const selectApprovalState =
  createFeatureSelector<ApprovalState>(APPROVAL_FEATURE_KEY);

/**
 * Basic Selectors
 */
export const selectApprovalRequests = createSelector(
  selectApprovalState,
  state => state.approvalRequests
);

export const selectLoading = createSelector(
  selectApprovalState,
  state => state.loading
);

export const selectError = createSelector(
  selectApprovalState,
  state => state.error
);

export const selectActiveMode = createSelector(
  selectApprovalState,
  state => state.activeMode
);

/**
 * Pagination Selectors
 */
export const selectPageIndex = createSelector(
  selectApprovalState,
  state => state.pageIndex
);

export const selectPageSize = createSelector(
  selectApprovalState,
  state => state.pageSize
);

export const selectTotal = createSelector(
  selectApprovalState,
  state => state.total
);

export const selectPagination = createSelector(
  selectPageIndex,
  selectPageSize,
  selectTotal,
  (pageIndex, pageSize, total) => ({
    pageIndex,
    pageSize,
    total,
  })
);

/**
 * Sort Selectors
 */
export const selectSortActive = createSelector(
  selectApprovalState,
  state => state.sortActive
);

export const selectSortDirection = createSelector(
  selectApprovalState,
  state => state.sortDirection
);

export const selectSort = createSelector(
  selectSortActive,
  selectSortDirection,
  (active, direction) => ({ active, direction })
);

/**
 * Filter Selectors
 */
export const selectFiltersByMode = createSelector(
  selectApprovalState,
  state => state.filtersByMode
);

export const selectCurrentFilters = createSelector(
  selectApprovalState,
  state => state.filtersByMode[state.activeMode] ?? {}
);

export const selectActiveFiltersCount = createSelector(
  selectCurrentFilters,
  filters => {
    if (!filters) return 0;
    return Object.values(filters).reduce((count: number, value) => {
      if (Array.isArray(value)) {
        return count + (value.length > 0 ? 1 : 0);
      }
      if (value instanceof Date) {
        return count + 1;
      }
      if (value === null || value === undefined || value === '') {
        return count;
      }
      return count + 1;
    }, 0);
  }
);

/**
 * Requesters Selectors — defined ahead of `selectFilterChips` because
 * the chip selector composes off `selectRequesters` and we can't rely
 * on hoisting now that everything in this file is a `const` selector.
 */
export const selectRequesters = createSelector(
  selectApprovalState,
  state => state.requesters
);

export const selectRequestersLoading = createSelector(
  selectApprovalState,
  state => state.requestersLoading
);

export const selectRequestersError = createSelector(
  selectApprovalState,
  state => state.requestersError
);

/**
 * Filter Chips Selector
 */
export const selectFilterChips = createSelector(
  selectCurrentFilters,
  selectRequesters,
  (filters, requesters) => {
    const chips: {
      key: string;
      label: string;
      value?: string;
      dateFrom?: string | Date | null;
      dateTo?: string | Date | null;
    }[] = [];

    // Date range chip — emit raw ISO dates; presentation/locale formatting
    // is the consumer's responsibility (see `LocalizedDatePipe` /
    // `formatLocalizedDate`).
    if (filters.createdFrom || filters.createdTo) {
      chips.push({
        key: 'dateRange',
        label: '',
        dateFrom: filters.createdFrom ?? null,
        dateTo: filters.createdTo ?? null,
      });
    }

    // Requester chips
    if (filters.requesterIds?.length) {
      const requesterMap = new Map(requesters.map(r => [r.userId, r.name]));
      for (const id of filters.requesterIds) {
        chips.push({
          key: 'requester',
          label: requesterMap.get(id) ?? id,
          value: id,
        });
      }
    }

    // Domain chip — label resolves against the i18n bundle in the consumer
    // (selectors stay pure / Translate-free); we only carry the raw enum
    // code here and let the component look it up.
    if (filters.domain) {
      chips.push({
        key: 'domain',
        label: filters.domain,
        value: filters.domain,
      });
    }

    // Status chip — same arrangement as `domain`.
    if (filters.status) {
      chips.push({
        key: 'status',
        label: filters.status,
        value: filters.status,
      });
    }

    return chips;
  }
);

export const selectVisibleFilterChips = createSelector(
  selectFilterChips,
  chips => chips.slice(0, 3)
);

export const selectExtraFilterCount = createSelector(
  selectFilterChips,
  chips => (chips.length > 3 ? chips.length - 3 : 0)
);

/**
 * Search Selector
 */
export const selectSearchTerm = createSelector(
  selectApprovalState,
  state => state.searchTerm
);

/**
 * Filter Modal Selector
 */
export const selectFilterModalOpen = createSelector(
  selectApprovalState,
  state => state.filterModalOpen
);

// ---------------------------------------------------------------------------
// Detail (my-requests audience)
// ---------------------------------------------------------------------------

export const selectSelectedRequest = createSelector(
  selectApprovalState,
  state => state.selectedRequest
);

export const selectDetailLoading = createSelector(
  selectApprovalState,
  state => state.detailLoading
);

export const selectDetailError = createSelector(
  selectApprovalState,
  state => state.detailError
);

// ---------------------------------------------------------------------------
// Cancel (my-requests audience)
// ---------------------------------------------------------------------------

export const selectCancelLoading = createSelector(
  selectApprovalState,
  state => state.cancelLoading
);

export const selectCancelError = createSelector(
  selectApprovalState,
  state => state.cancelError
);
